import React, { useCallback, FormEvent } from 'react'
import {
    Box,
    Button,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    Stack,
    SxProps,
    Theme,
    Typography,
} from '@mui/material'
import DraggableDialogTitle from './DraggableDialogTitle'
import { ExtraSmallButton } from '../custom'
import ClearIcon from '../../assets/icons/ClearIcon'
import CustomDialog from './CustomDialog'
import { useConfirmationDialogKeyboard } from './useConfirmationDialogKeyboard'

type ReusableDialogProps = {
    open: boolean
    onClose: () => void
    onSubmit: () => void | Promise<void>
    title?: string
    content: string | any
    cancelVariant?: 'text' | 'outlined' | 'contained'
    submitVariant?: 'text' | 'outlined' | 'contained'
    cancelText?: string
    submitText?: string
    cancelButtonProps?: ButtonProps
    submitButtonProps?: ButtonProps
    fullWidth?: boolean
    maxWidth?: DialogProps['maxWidth']
    buttonSize?: 'small' | 'medium' | 'large'
    dialogContentSx?: SxProps<Theme>
    dialogActionsSx?: SxProps<Theme>
    /** Non-modal: chart/page stays interactive; uses CustomDialog. Default true. */
    nonModal?: boolean
    container?: DialogProps['container']
}

const ReusableDialog: React.FC<ReusableDialogProps> = ({
    open,
    onClose,
    onSubmit,
    title,
    content,
    cancelVariant = 'text',
    submitVariant = 'contained',
    cancelText = 'Cancel',
    submitText = 'Submit',
    cancelButtonProps,
    submitButtonProps,
    fullWidth = true,
    maxWidth = 'sm',
    buttonSize = 'small',
    dialogContentSx,
    dialogActionsSx,
    nonModal = true,
    container,
}) => {
    const handleSubmit = useCallback(async () => {
        try {
            await Promise.resolve(onSubmit())
            onClose()
        } catch {
            // Keep dialog open when onSubmit rejects (e.g. failed API call).
        }
    }, [onSubmit, onClose])

    const submitDisabled = Boolean(submitButtonProps?.disabled)

    const handleFormSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault()
            if (submitDisabled) return
            handleSubmit()
        },
        [handleSubmit, submitDisabled]
    )

    useConfirmationDialogKeyboard(open && !nonModal, {
        onSubmit: handleSubmit,
        onClose,
        submitDisabled,
    })

    const dialogBody = (
        <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'contents' }}>
            {title && (
                <DraggableDialogTitle draggable={false}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant={'h6'}>{title}</Typography>
                        <ExtraSmallButton onClick={onClose} aria-label="close" color={'inherit'}>
                            <ClearIcon />
                        </ExtraSmallButton>
                    </Stack>
                </DraggableDialogTitle>
            )}
            <DialogContent
                sx={dialogContentSx ?? (!title ? { p: 2 } : content ? {} : { py: 0, px: 2 })}
            >
                {content &&
                    (typeof content === 'string' ? (
                        <Typography variant={'body2'}>{content}</Typography>
                    ) : (
                        content
                    ))}
            </DialogContent>
            <DialogActions sx={dialogActionsSx ?? { px: 2, py: 1 }}>
                <Button
                    type="button"
                    onClick={onClose}
                    variant={cancelVariant}
                    size={buttonSize}
                    {...cancelButtonProps}
                >
                    {cancelText}
                </Button>
                <Button type="submit" variant={submitVariant} size={buttonSize} {...submitButtonProps}>
                    {submitText}
                </Button>
            </DialogActions>
        </Box>
    )

    const sharedProps = {
        open,
        onClose,
        maxWidth,
        fullWidth,
        container,
    }

    if (nonModal) {
        return (
            <CustomDialog
                {...sharedProps}
                onEnterSubmit={handleSubmit}
                enterSubmitDisabled={submitDisabled}
            >
                {dialogBody}
            </CustomDialog>
        )
    }

    return <Dialog {...sharedProps}>{dialogBody}</Dialog>
}

export default ReusableDialog
